import { assertNever } from '@framework/engine';
import EsriJSON from 'ol/format/EsriJSON';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import { tile as tileStrategy } from 'ol/loadingstrategy';
import { TileArcGISRest, TileWMS } from 'ol/source';
import OSM from 'ol/source/OSM';
import VectorSource from 'ol/source/Vector';
import XYZ from 'ol/source/XYZ';
import { Fill, Stroke, Style, Text } from 'ol/style';
import { createXYZ } from 'ol/tilegrid';
import { DefaultMapLayerMinZoom, IMapLayer, MapLayerType } from '../MapLayer';

export function normalizeLayerUrl(url: string) {
  if (!url) return url;
  return url.endsWith('/') ? url.substring(0, url.length - 1) : url;
}

/**
 * Converte un layer OSM in un layer OpenLayers
 */
export function creteMapLayerFromConfig(layer: IMapLayer) {
  switch (layer.type) {
    case MapLayerType.OSM:
      return new TileLayer({
        source: new OSM(),
        opacity: layer.style?.opacity,
      });

    case MapLayerType.XYZ: {
      let url = normalizeLayerUrl(layer.url);
      url = url?.includes('{x}') ? url : url + '{z}/{y}/{x}';

      return new TileLayer({
        source: new XYZ({
          url,
          attributions: layer.name,
        }),
        opacity: layer.style?.opacity,
      });
    }

    case MapLayerType.ArcGISRestTile: {
      const url = normalizeLayerUrl(layer.url);
      return new TileLayer({
        source: new XYZ({
          url: url + '/tile/{z}/{y}/{x}',
          attributions: layer.name,
        }),
        opacity: layer.style?.opacity,
      });
    }

    case MapLayerType.WMS: {
      const url = normalizeLayerUrl(layer.url);
      return new TileLayer({
        // extent: [-13884991, 2870341, -7455066, 6338219],
        source: new TileWMS({
          url,
          params: { LAYERS: '', TILED: true },
          // serverType: 'geoserver', // TODO Configurazione
          transition: 0,
        }),
      });
    }

    case MapLayerType.ArcGISRest: {
      const url = normalizeLayerUrl(layer.url);
      return new TileLayer({
        source: new TileArcGISRest({
          url,
        }),
        opacity: layer.style?.opacity,
      });
    }

    case MapLayerType.ArcGISRestVector: {
      const baseUrl = normalizeLayerUrl(layer.url);
      const source = new VectorSource({
        format: new EsriJSON({}),
        url: function (extent, resolution, projection) {
          // ArcGIS Server vuole solamente la parte numerica del SRID
          const srid = 102100;
          const url =
            baseUrl +
            '/query/?f=json&' +
            'returnGeometry=true&spatialRel=esriSpatialRelIntersects&geometry=' +
            encodeURIComponent(
              '{"xmin":' +
                extent[0] +
                ',"ymin":' +
                extent[1] +
                ',"xmax":' +
                extent[2] +
                ',"ymax":' +
                extent[3] +
                '}',
            ) +
            '&geometryType=esriGeometryEnvelope&inSR=' +
            srid +
            '&outFields=*' +
            '&outSR=' +
            srid;

          return url;
        },
        // strategy: bboxStrategy,
        strategy: tileStrategy(
          createXYZ({
            tileSize: 512,
          }),
        ),
      });

      const style = createVectorStyle(layer);

      return new VectorLayer({
        minZoom: layer.minZoom ?? DefaultMapLayerMinZoom,
        source,
        opacity: layer.style?.opacity,
        style: function (feature) {
          // Se non è definita una proprietà per l'etichetta, restituiamo lo
          // stile di default uguale per tutte le feature
          if (!layer.labelPropertyName) return style;

          const featureStyle = style.clone();
          featureStyle.setText(
            new Text({
              fill: new Fill({
                color: 'black',
              }),
              text: feature.getProperties()?.[layer.labelPropertyName] ?? '',
            }),
          );
          return featureStyle;
        },
      });
    }

    case MapLayerType.Vector: {
      throw new Error('Not implemented');
    }

    default:
      assertNever(layer.type);
  }
}

export function createVectorStyle(layer: IMapLayer) {
  // const [r, g, b] = Array.from(color.asArray(layer.style?.fillColor ?? '#ccc'));
  const style = new Style({
    fill: new Fill({
      color: layer.style?.fillColor,
    }),
    stroke: new Stroke({
      color: layer.style?.strokeColor,
      width: layer.style?.strokeWidth,
    }),
  });

  return style;
}
